import {
  Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { ReplaySubject } from 'rxjs';

export interface DnaPageEvent {
  pageIndex: number;
  pageSize: number;
  length: number;
}

@Component({
  selector: 'dna-paginator',
  templateUrl: './dna-paginator.component.html',
  styleUrls: ['./dna-paginator.component.scss'],
  standalone: false
})
export class DnaPaginatorComponent implements OnChanges, OnInit {
  @Input() length = 0;
  @Input() pageSize = 10;
  @Input() pageIndex = 0;
  @Input() pageSizeOptions: number[] = [5, 10, 25, 50];
  @Input() showFirstLastButtons = true;

  @Output() page = new EventEmitter<DnaPageEvent>();

  // Compatibilidade com MatTableDataSource.paginator (drop-in de MatPaginator)
  readonly initialized = new ReplaySubject<void>(1);

  ngOnInit(): void {
    this.initialized.next();
  }

  pages: number[] = [];

  get safeLength(): number {
    return Number.isFinite(this.length) && this.length > 0 ? this.length : 0;
  }

  get totalPages(): number {
    return Math.ceil(this.safeLength / this.pageSize) || 1;
  }

  get rangeStart(): number {
    return this.safeLength === 0 ? 0 : this.pageIndex * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.safeLength);
  }

  get visiblePages(): number[] {
    const total = this.totalPages;
    const current = this.pageIndex;
    const pages: number[] = [];

    if (total <= 7) {
      for (let i = 0; i < total; i++) pages.push(i);
      return pages;
    }

    // Always show first, last, current ±1, with ellipsis
    const range = new Set<number>();
    range.add(0);
    range.add(total - 1);
    for (let i = Math.max(0, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      range.add(i);
    }

    const sorted = Array.from(range).sort((a, b) => a - b);
    const result: number[] = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
        result.push(-1); // ellipsis marker
      }
    }
    return result;
  }

  ngOnChanges(_: SimpleChanges): void {}

  goTo(pageIndex: number): void {
    if (pageIndex < 0 || pageIndex >= this.totalPages) return;
    this.pageIndex = pageIndex;
    this.emit();
  }

  changeSize(event: Event): void {
    this.pageSize = +(event.target as HTMLSelectElement).value;
    this.pageIndex = 0;
    this.emit();
  }

  private emit(): void {
    this.page.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize, length: this.length });
  }
}
